import { Button, Col, Form, Row, Select, message } from "antd";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { InvoiceCreateFormColumns } from "../Page/InvoiceCreateFormColumns";
import { useState, useEffect, useMemo } from "react";
import { PlusOutlined, UnorderedListOutlined } from "@ant-design/icons";
import useGet from "../../../global/hooks/useGet";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import PaginationTable from "../../../global/components/paginationTable";
import dayjs from "dayjs";
import usePost from "../../../global/hooks/usePost";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { useNavigate } from "react-router-dom";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import { hasRole } from "../../../global/hooks/getUserRole";

const initialFilterState = {
  OemNo: "",
  DealerNo: "",
  Month: dayjs().format("YYYY-MM"),
};

const InvoiceCreateForm = () => {
  const navigate = useNavigate();
  const logData = JSON.parse(sessionStorage.getItem("userData"));
  const [hoveredRow, setHoveredRow] = useState(null);
  const [filter, setFilter] = useState(initialFilterState);
  const [params, setParams] = useState(
    hasRole(1, 2, 3)
      ? {
          limit: 10,
          sort_by: "DealerNo",
          order: "desc",
          OemNo: logData?.oem_no,
          DealerNo: logData?.dealer_no,
        }
      : {
          limit: 10,
          sort_by: "DealerNo",
          order: "desc",
        },
  ); // Initialize with default params structure

  const initialPagination = {
    current: 1,
    pageSize: 10,
    total: 0,
  };

  const [form] = Form.useForm();
  const role_value = logData?.role_value;
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [listData, setListData] = useState([]);
  const [shouldFetchData, setShouldFetchData] = useState(false); // Control API calls

  // console.log("logData", logData);

  // API call for generate invoice data
  const postUrl = `${process.env.REACT_APP_API_URL}/api/invoices/create-invoice/`;

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);

  // Build query string from params - only call API if shouldFetchData is true and Month is present
  const query = params ? new URLSearchParams(params).toString() : null;
  const invoiceUrl = shouldFetchData
    ? `${process.env.REACT_APP_API_URL}/api/invoices/invoice-dealers/?${query}`
    : null;

  const {
    data: invoiceData,
    loading: invoiceDataLoading,
    error: invoiceDataError,
    refetch: invoiceDataRefetch,
  } = useGet(invoiceUrl);

  // API call for options (OEM and Dealer dropdowns)
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/accounts/options/`;
  const {
    data: optionData,
    loading: optionDataLoading,
    error: optionDataError,
  } = useGet(optionUrl);

  const [modifiedOptions, setModifiedOptions] = useState({
    oems: [],
    dealers: [],
  });

  const originalOptions = useMemo(
    () => ({
      oems: getModifiedResponse(optionData, "oems") || [],
      dealers: getModifiedResponse(optionData, "dealers") || [],
    }),
    [optionData],
  );

  useEffect(() => {
    setModifiedOptions(originalOptions);
  }, [originalOptions]);

  // Update list data when invoice data changes
  useEffect(() => {
    if (invoiceData && !invoiceDataLoading) {
      const newData = invoiceData?.data?.map((el, index) => ({
        ...el,
        key: el.DealerNo,
      }));
      setListData(newData);
    }
  }, [invoiceData, invoiceDataLoading]);

  const validSelectedRowKeys = useMemo(() => {
    return selectedRowKeys.filter((key) => {
      const record = listData.find((item) => item.DealerNo === key);
      return record && record.Month === null; // Only include keys where Month is null
    });
  }, [selectedRowKeys, listData]);

  const onSelectChange = (newSelectedRowKeys, newSelectedRow) => {
    // console.log("selectedRowKeys changed: ", newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  // console.log(selectedRowKeys, "selectedRowKeys");

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
    hideSelectAll: false,
    renderCell: (checked, record, index, originNode) => {
      // Hide checkbox if Month exists
      if (record.Month !== null && record.Month !== undefined) {
        return null;
      }
      return originNode;
    },
  };

  const onRow = (record) => {
    return {
      onClick: (event) => {
        // navigate(`/invoice/info/${record?.InvoiceNo}`);
      },
    };
  };

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: invoiceData?.count || 0,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100, 500, 1000],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...params,
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  };

  const handleReset = () => {
    const initialMonth = dayjs();
    form.resetFields();
    form.setFieldsValue({ Month: initialMonth });
    setFilter({ ...initialFilterState, Month: initialMonth.format("YYYY-MM") });
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    setParams(
      hasRole(7, 8, 9)
        ? {
            limit: 10,
            sort_by: "DealerNo",
            order: "desc",
            Month: initialMonth.format("YYYY-MM"),
            OemNo: logData?.oem_no,
            DealerNo: logData?.dealer_no,
          }
        : {
            limit: 10,
            sort_by: "DealerNo",
            order: "desc",
            Month: initialMonth.format("YYYY-MM"),
          },
    ); // Reset to default params
    setShouldFetchData(true);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    const previousMonth = dayjs(filter.Month, "YYYY-MM", true).isValid()
      ? dayjs(filter.Month, "YYYY-MM")
      : dayjs();
    setFilter((prev) => ({ ...prev, Month: previousMonth.format("YYYY-MM") }));
    form.setFieldsValue({ Month: previousMonth });
    setShouldFetchData(true); // Enable API calls
    setParams(
      hasRole(7, 8, 9)
        ? {
            limit: 10,
            sort_by: "DealerNo",
            order: "desc",
            Month: previousMonth.format("YYYY-MM"),
            OemNo: logData?.oem_no,
            DealerNo: logData?.dealer_no,
          }
        : {
            limit: 10,
            sort_by: "DealerNo",
            order: "desc",
            Month: previousMonth.format("YYYY-MM"),
          },
    );
  }, []);

  const handleFilter = () => {
    // Check if Month is selected
    if (!filter.Month) {
      form.setFields([
        {
          name: "Month",
          errors: ["Month is required"],
        },
      ]);
      return;
    }

    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        filterDict[key] = filter[key];
      }
    }
    // Month is required, so it should always be in filterDict
    const newParams = hasRole(7, 8, 9)
      ? {
          limit: 10,
          sort_by: "DealerNo",
          order: "desc",
          ...filterDict,
          OemNo: logData?.oem_no,
          DealerNo: logData?.dealer_no,
        }
      : {
          limit: 10,
          sort_by: "DealerNo",
          order: "desc",
          ...filterDict,
        };
    setParams(newParams);
    setShouldFetchData(true); // Enable API calls
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
  };

  const onFinish = (values) => {
    const { Month } = values;
    const formattedMonth = dayjs(Month).format("YYYY-MM");
    const data = {
      month: formattedMonth,
      dealer_ids: validSelectedRowKeys,
    };
    postAction(data);
  };

  //toat for create
  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      openConfirmNotification("Success");
      // invoiceListRefecth();
      invoiceDataRefetch();
      // setTimeout(() => {
      // setShowCommonModal(false);
      // handleReset();
      // form.resetFields();
      setSelectedRowKeys([]);
      // }, 500);
    }
  }, [postError, postData, postLoading]);
  const onSearch = (value) => {
    console.log("search:", value);
  };

  const buttonsData = [
    {
      label: "List",
      icon: <UnorderedListOutlined className="" />,
      onClick: () => {
        navigate(-1);
      },
    },
  ];
  const breadcrumbItems = [
    {
      label: "Invoice",
      href: "/invoice",
    },
    {
      label: "Create",
      href: `/invoice/add`,
    },
  ];

  return (
    <>
      <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
          <CommonButton buttonsData={buttonsData} />
        </CommonBreadcrumb>
      </div>
      <div className="border bg-white rounded-[4px] p-4 overflow-hidden">
        <div className="">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={8} xl={8}>
              <Form form={form}>
                <CustomDateTimePicker
                  rules={[{ required: true, message: "Month is required" }]}
                  name="Month"
                  placeholder="Month *"
                  picker="month"
                  onChange={(value) => {
                    if (value) {
                      const formattedMonth = dayjs(value).format("YYYY-MM");
                      setFilter({ ...filter, Month: formattedMonth });
                    } else {
                      setFilter({ ...filter, Month: "" });
                    }
                  }}
                />
              </Form>
            </Col>
            {![4, 5, 6, 7, 8, 9, 10, 11, 12].includes(logData?.role_value) && (
              <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                <Select
                  showSearch
                  allowClear
                  placeholder="OEM"
                  optionFilterProp="label"
                  value={filter.OemNo || undefined}
                  onChange={(value) => setFilter({ ...filter, OemNo: value })}
                  onSearch={onSearch}
                  options={modifiedOptions.oems}
                  style={{ width: "100%" }}
                />
              </Col>
            )}

            {![7, 8, 9].includes(logData?.role_value) && (
              <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                <Select
                  showSearch
                  allowClear
                  placeholder="Dealer"
                  optionFilterProp="label"
                  value={filter.DealerNo ? Number(filter.DealerNo) : undefined}
                  onChange={(value) =>
                    setFilter({ ...filter, DealerNo: value })
                  }
                  onSearch={onSearch}
                  options={modifiedOptions.dealers}
                  style={{ width: "100%" }}
                />
              </Col>
            )}
          </Row>
        </div>
        <div className="flex justify-between my-4 overflow-x-auto !items-center">
          <div className="flex items-center">
            <Button
              type="primary"
              className="mr-2 w-[104px] h-[30px] font-medium"
              onClick={handleFilter}
              disabled={!filter.Month}
            >
              <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
              <span>Apply</span>
            </Button>
            <Button
              className="reset-button w-[104px] h-[30px] font-medium"
              onClick={handleReset}
            >
              <HiArrowPath className="!text-[14px] font-medium" />
              Reset
            </Button>

            {[1, 2, 3, 7, 8, 9].includes(role_value) && (
              <Button
                type="primary"
                className="w-fit] h-[32px] font-medium -ml-2"
                disabled={validSelectedRowKeys?.length === 0}
                onClick={() => {
                  const formValues = form.getFieldsValue();
                  onFinish(formValues);
                }}
                loading={postLoading}
              >
                <PlusOutlined className="" />
                Generate Invoice
              </Button>
            )}
          </div>
        </div>

        <GlobalTable
          columns={InvoiceCreateFormColumns(
            setParams,
            params,
            logData?.role_value === 1 || logData?.role_value === 3,
            logData?.role_value === 3,
          )}
          dataSource={listData}
          rowSelection={rowSelection}
          pagination={false}
          onRow={(record, rowIndex) => {
            return {
              onMouseEnter: () => setHoveredRow(rowIndex),
              onMouseLeave: () => setHoveredRow(null),
              ...onRow?.(record, rowIndex),
            };
          }}
          loading={invoiceDataLoading}
        />

        {!invoiceDataLoading &&
          paginationConfig !== false &&
          paginationConfig?.total > 0 && (
            <PaginationTable
              total={paginationConfig.total}
              pagination={paginationConfig}
              paginationConfig={paginationConfig}
              width="!w-[75px] !text-center"
            />
          )}
      </div>
    </>
  );
};

export default InvoiceCreateForm;
